import React, { ReactNode, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import ShareIcon from '@mui/icons-material/Share';

import { buttonVariants, buttonWidths } from '@clatter/ui';
import { useDeepCompareMemo, useMountedEffect, userRolesMap } from '@clatter/platform';
import { useShareDialog } from '../../hooks';
import { aclRolesMap, ServiceTypes } from '../../constants';
import { baseApi } from '../../services/baseApi';
import { FolderManagerItemTypes } from '../../interfaces';
import { useUpdateProjectMutation } from '../../services';

import { ProjectsLeftRail } from './components/ProjectsLeftRail';
import { FolderManagerShareDialog } from '../../components/FolderManager/Components/FolderManagerShareDialog';
import { NgEditablePageTitle, NgGridLayout, NgButton, AccessGuard } from '../../components';
import { ProjectWorkbookUploadEventBus } from '../../events/project-workbook-upload';
import { authSelectors } from '../../store';

interface ProjectsLayoutProps {
  children: ReactNode;
  customContent?: ReactNode | null | undefined;
  projectSideNavItems: ProjectSideNavItem[];
  grants?: UserGrants;
  isLoading?: boolean;
  projectData: any;
}

const StyledHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;

  > *:first-child {
    flex: 0.6;
  }

  @media (max-width: 800px) {
    flex-direction: column;
  }
`;

export const ProjectsLayout: React.FC<ProjectsLayoutProps> = ({
  children,
  isLoading: isParentLoading,
  projectSideNavItems,
  grants,
  projectData,
}) => {
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const activeUser = useSelector(authSelectors.getUserAuthState)?.user;

  const { shareDialogState, openShareDialog, closeShareDialog } = useShareDialog();

  const [updateProject, { isLoading: isUpdateProjectLoading }] = useUpdateProjectMutation();

  const isLoading = isParentLoading || isUpdateProjectLoading;

  //region METHODS
  const handleShare = () => {
    const workspaceData: Partial<FolderManagerItem> = {
      type: FolderManagerItemTypes.WORKSPACE,
      id: projectData.ID,
      displayName: projectData.DisplayName,
      name: projectData.DisplayName,
    };

    openShareDialog([workspaceData as FolderManagerItem]);
  };

  const revalidateProjectState = useCallback(async () => {
    await dispatch(baseApi.util.invalidateTags([{ type: ServiceTypes.PROJECTS, id: projectId }]));
    await dispatch(baseApi.util.invalidateTags([{ type: ServiceTypes.CCMS }]));
    await dispatch(baseApi.util.invalidateTags([{ type: 'Folders' }]));
    await dispatch(baseApi.util.invalidateTags([{ type: 'Types' }]));
  }, [dispatch, projectId]);

  const handleWorkbookUploadComplete = async () => {
    // refetch project data, so we can update stepper status indicators
    ProjectWorkbookUploadEventBus.publish('@clatter/PROJECT_WORKBOOK_UPLOAD_COMPLETED', undefined);
    await revalidateProjectState();
  };

  const handleUpdateProjectDisplayName = async (value: string) => {
    const trimmed = value?.trim();

    await updateProject({
      projectId: projectId,
      data: {
        displayName: trimmed,
      },
    }).unwrap();

    await revalidateProjectState();
  };
  //endregion

  useMountedEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const disableEdit = useDeepCompareMemo(
    () =>
      !AccessGuard.check({
        allowedRoles: [userRolesMap.clatterAdmin, aclRolesMap.DATA_ADMIN, aclRolesMap.EDITOR],
        accessRole: grants?.role,
        user: activeUser,
      }),
    [projectData, grants, activeUser],
  );

  useEffect(() => {
    const unsubscribe = ProjectWorkbookUploadEventBus.subscribe(
      '@clatter/PROJECT_WORKBOOK_UPLOAD_COMPLETED',
      revalidateProjectState,
    );

    return () => {
      unsubscribe();
    };
  }, [revalidateProjectState]);

  return (
    // <AssetManagerLayout contentPadding={'0'} activeSlug="my-workspace">
    <>
      <StyledHeaderContainer>
        <NgEditablePageTitle
          title={projectData?.DisplayName}
          isLoading={isLoading}
          onUpdate={handleUpdateProjectDisplayName}
          disableEdit={disableEdit}
        />

        <NgButton width={buttonWidths.sm} variant={buttonVariants.clear} onClick={handleShare} icon={ShareIcon}>
          Share
        </NgButton>
      </StyledHeaderContainer>

      <NgGridLayout>
        <aside>
          <ProjectsLeftRail
            grants={grants}
            isLoading={isLoading}
            projectData={projectData}
            projectSideNavItems={projectSideNavItems}
            onWorkbookUploadComplete={handleWorkbookUploadComplete}
          />
        </aside>

        <div>{children}</div>
      </NgGridLayout>

      {shareDialogState.opened && (
        <FolderManagerShareDialog
          opened={shareDialogState.opened}
          selectedRows={shareDialogState.selectedItems}
          onDismiss={() => closeShareDialog()}
          prefix="workspaces"
        />
      )}
    </>
  );
};
